import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import {
  CreativeAccordion,
  CreativeAccordionSummary,
  CreativeAppName,
  CreativePaper,
} from 'features/creatives';
import { ExpandMoreOutlined, ZoomOutMapOutlined } from '@mui/icons-material';
import type { GoogleCreative } from '../api/types';
import GoogleAccordionDetails from './GoogleAccordionDetails';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';
import GoogleExpandedCreativeDialog from './GoogleExpandedCreativeDialog';
import GoogleCreativeVariationsCarousel from './GoogleCreativeVariationsCarousel';
import { trackAdsLibraryCreativeView } from 'lib/datadog';

interface GoogleCreativeItemProps {
  creative: GoogleCreative;
}

const GoogleCreativeItem: FC<GoogleCreativeItemProps> = ({ creative }) => {
  const { allExpanded, network, region } = useFacebookAdsLibraryParams();
  const [expanded, setExpanded] = useState(false);
  const [expandedViewOpen, setExpandedViewOpen] = useState(false);

  const winnerConfidencePercent = Math.round(creative.winnerConfidenceScore * 100);
  const firstVariationWithClick = creative.variations.find((variation) => !!variation.clickUrl);
  const isVideo = creative.format === 'video';

  useEffect(() => {
    requestAnimationFrame(() => {
      setExpanded(allExpanded);
    });
  }, [allExpanded]);

  const handleExpansion = useCallback(() => {
    setExpanded((prev) => {
      const nextExpanded = !prev;
      if (nextExpanded) {
        trackAdsLibraryCreativeView({
          network,
          region,
          viewType: 'accordion_expand',
          creativeId: creative.adCreativeId,
          company: creative.company || creative.advertiserName,
          adCreativeType: creative.format,
        });
      }
      return nextExpanded;
    });
  }, [creative.adCreativeId, creative.advertiserName, creative.company, creative.format, network, region]);

  const handleOpenExpandedView = useCallback(() => {
    trackAdsLibraryCreativeView({
      network,
      region,
      viewType: 'dialog_open',
      creativeId: creative.adCreativeId,
      company: creative.company || creative.advertiserName,
      adCreativeType: creative.format,
    });
    setExpandedViewOpen(true);
  }, [creative.adCreativeId, creative.advertiserName, creative.company, creative.format, network, region]);

  return (
    <CreativePaper
      id={`google-ads-creative-${creative.adCreativeId}-paper`}
      sx={{
        margin: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'start',
          p: 1,
          gap: 0.75,
        }}
      >
        {/* Title row: company name + 3 metric chips */}
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'row',
            alignItems: 'start',
            justifyContent: 'space-between',
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              flex: 1,
              maxWidth: '35%',
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'start',
              position: 'relative',
            }}
          >
            <Tooltip title={creative.company || creative.advertiserName || 'Unknown company'}>
              <CreativeAppName>{creative.company || creative.advertiserName || 'Unknown company'}</CreativeAppName>
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexWrap: 'nowrap', justifyContent: 'end' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', justifyContent: 'end' }}>
              <Chip
                size="small"
                label={`${winnerConfidencePercent}%`}
                sx={{ flex: 1, backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: 1 }}
              />
              <Chip
                size="small"
                label={`${creative.longevity}d`}
                sx={{
                  flex: 1,
                  backgroundColor: '#E3F2FD',
                  color: '#1565C0',
                  fontWeight: 600,
                  borderRadius: 1,
                }}
              />
              <Tooltip title={`Number of variations`}>
                <Chip
                  size="small"
                  label={`${creative.variationsCount} var.`}
                  sx={{
                    position: 'relative',
                    flex: 1,
                    backgroundColor: '#F3E5F5',
                    color: '#6A1B9A',
                    fontWeight: 600,
                    borderRadius: 1,
                    '& .MuiChip-label': {
                      maxWidth: '100%',
                      display: 'block',
                    },
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Accordion with expand arrows + accordion details */}
        <CreativeAccordion expanded={expanded} onChange={handleExpansion}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Tooltip title="Expand creative">
              <IconButton
                size="small"
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenExpandedView();
                }}
              >
                <ZoomOutMapOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <CreativeAccordionSummary>
              <IconButton size="small" sx={{ p: 0 }}>
                <ExpandMoreOutlined
                  sx={{ transition: 'transform 0.2s', transform: `rotate(${expanded ? 180 : 0}deg)` }}
                />
              </IconButton>
            </CreativeAccordionSummary>
          </Box>
          <GoogleAccordionDetails
            domains={creative.domains}
            firstShownOn={creative.firstShownOn}
            lastShownOn={creative.lastShownOn}
            clickUrl={firstVariationWithClick?.clickUrl}
          />
        </CreativeAccordion>
      </Box>

      {/* Creative image/video — video uses a fixed height, images auto-fit their natural ratio */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          ...(isVideo ? { height: 300 } : {}),
        }}
      >
        <GoogleCreativeVariationsCarousel
          creative={creative}
          containerId={`google-ads-creative-${creative.adCreativeId}-paper`}
          autoHeight={!isVideo}
          hoverControls
        />
      </Box>

      {expandedViewOpen && (
        <GoogleExpandedCreativeDialog
          open={expandedViewOpen}
          onClose={() => {
            setExpandedViewOpen(false);
          }}
          creative={creative}
        />
      )}
    </CreativePaper>
  );
};

export default GoogleCreativeItem;
